/** Skeletons — design spec §7. The shape of the content, never a spinner. */

import { cn } from "@/lib/cn";

/**
 * §5 — pulse is 1.5s, and each row starts 0.1s after the one above it so the
 * list breathes rather than blinking as one block.
 */
function pulseDelay(index: number): { animationDelay: string } {
  return { animationDelay: `${index * 0.1}s` };
}

/** An inner text line: `--sep`, at one of the spec's three widths. */
export function SkeletonLine({
  width = "80%",
  index = 0,
  className,
}: {
  width?: string;
  index?: number;
  className?: string;
}) {
  return (
    <div
      className={cn("h-3 animate-sk rounded-sm bg-sep", className)}
      style={{ width, ...pulseDelay(index) }}
      aria-hidden="true"
    />
  );
}

/** A block placeholder carrying the radius of the element it stands in for. */
export function SkeletonBlock({
  className,
  index = 0,
}: {
  className?: string;
  index?: number;
}) {
  return (
    <div
      className={cn("animate-sk rounded-card bg-sbg", className)}
      style={pulseDelay(index)}
      aria-hidden="true"
    />
  );
}

/** §7 — card skeleton: the 80/52/45% line widths the spec calls out. */
export function SkeletonCard({ index = 0, className }: { index?: number; className?: string }) {
  return (
    <div
      className={cn("animate-sk rounded-card bg-sbg p-3", className)}
      style={pulseDelay(index)}
      aria-hidden="true"
    >
      <div className="flex flex-col gap-2">
        <div className="h-3.5 rounded-sm bg-sep" style={{ width: "80%" }} />
        <div className="h-3 rounded-sm bg-sep" style={{ width: "52%" }} />
        <div className="h-3 rounded-sm bg-sep" style={{ width: "45%" }} />
      </div>
    </div>
  );
}

/**
 * §7 — four cards in a loading list, the last at .6 so the list reads as
 * continuing past the fold rather than ending there.
 */
export function SkeletonList({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-2", className)} role="status" aria-label="Загрузка">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} index={i} className={i === count - 1 ? "opacity-50" : undefined} />
      ))}
    </div>
  );
}

/** Header placeholder: screen title plus the meta line above it. */
export function SkeletonHeader() {
  return (
    <div className="flex flex-col gap-2" aria-hidden="true">
      <SkeletonBlock className="h-3 w-24" />
      <SkeletonBlock className="h-6 w-2/3" index={1} />
    </div>
  );
}

/** §9 — the three stat tiles on the dashboard while they load. */
export function SkeletonStats() {
  return (
    <div className="grid grid-cols-3 gap-2" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <SkeletonBlock key={i} className="h-[62px]" index={i} />
      ))}
    </div>
  );
}
